'use client';

import type { Asset } from '@/modules/Asset/interface/Asset';
import type { AssetsRecommendationAssetItem } from '@/modules/Asset/interface/Recommendation';
import { useSearchParams } from 'next/navigation';
import { type Dispatch, type PropsWithChildren, type SetStateAction, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ConnectInstitutionsModal } from '@/modules/Wallet/ui/ConnectInstitutionsModal';
import { SectionModalInvest } from '@/components/molecules/SectionModalInvest';
import { MODAL_CONNECTORS_ID, MODAL_INVEST_ID } from '@/viewModels/InvestViewModel/utils';
import { IfRender } from '@/utils/jsx';
import { useAuth } from './AuthContext';
export const InvestContext = createContext({} as InvestContextData);
export function InvestProvider({
  children
}: Readonly<PropsWithChildren>) {
  const investOpenParam = useSearchParams().get('invest_open');
  const {
    user
  } = useAuth();
  const [selectedAsset, setSelectedAsset] = useState<SelectedAsset>(null);
  const [answeredQuestionLP, setAnsweredQuestionLP] = useState(false);
  const closeInvestDrawer = () => window.dispatchEvent(new Event(`close-chakra-modal-${MODAL_INVEST_ID}`));
  const openInvestDrawer = () => window.dispatchEvent(new Event(`open-chakra-modal-${MODAL_INVEST_ID}`));
  const closeConnectorsDrawer = () => window.dispatchEvent(new Event(`close-chakra-modal-${MODAL_CONNECTORS_ID}`));
  const openConnectorsDrawer = () => window.dispatchEvent(new Event(`open-chakra-modal-${MODAL_CONNECTORS_ID}`));
  const selectAsset = useCallback(({
    asset,
    scrollUp = false,
    openDrawer = false
  }: SelectAssetParams) => {
    setSelectedAsset(asset);
    if (openDrawer) {
      return openInvestDrawer();
    }
    if (!scrollUp) {
      window.dispatchEvent(new Event(`action-back-button-drawer-${MODAL_INVEST_ID}`));
      return;
    }
    const section = document.getElementById('section-send-order-advisor');
    if (section) {
      section.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, []);
  const openDrawerIfInvestParam = () => {
    if (investOpenParam && investOpenParam === '1') {
      openInvestDrawer();
    }
  };
  const value = useMemo(() => ({
    answeredQuestionLP,
    selectedAsset,
    closeConnectorsDrawer,
    closeInvestDrawer,
    openConnectorsDrawer,
    openInvestDrawer,
    selectAsset,
    setAnsweredQuestionLP
  }), [answeredQuestionLP, selectAsset, selectedAsset]);
  useEffect(() => {
    window.addEventListener(`loaded-chakra-modal-${MODAL_INVEST_ID}`, openDrawerIfInvestParam);
    return () => {
      window.removeEventListener(`loaded-chakra-modal-${MODAL_INVEST_ID}`, openDrawerIfInvestParam);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <InvestContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="InvestProvider" data-sentry-source-file="InvestContext.tsx">
      <>
        <IfRender condition={!!user?.name} data-sentry-element="IfRender" data-sentry-source-file="InvestContext.tsx">
          <>
            <SectionModalInvest data-sentry-element="SectionModalInvest" data-sentry-source-file="InvestContext.tsx" />
            <ConnectInstitutionsModal data-sentry-element="ConnectInstitutionsModal" data-sentry-source-file="InvestContext.tsx" />
          </>
        </IfRender>
        {children}
      </>
    </InvestContext.Provider>;
}
export const useInvest = () => useContext(InvestContext);
export type SelectedAsset = Omit<Asset, 'type'> | Omit<AssetsRecommendationAssetItem, 'type'> | null;
type SelectAssetParams = {
  asset: SelectedAsset;
  scrollUp?: boolean;
  openDrawer?: boolean;
};
type InvestContextData = {
  answeredQuestionLP: boolean;
  selectedAsset: SelectedAsset;
  closeConnectorsDrawer: () => void;
  closeInvestDrawer: () => void;
  openConnectorsDrawer: () => void;
  openInvestDrawer: () => void;
  selectAsset: ({
    asset,
    scrollUp,
    openDrawer
  }: SelectAssetParams) => void;
  setAnsweredQuestionLP: Dispatch<SetStateAction<boolean>>;
};